import { useEffect, useState } from 'react';

import { Icon } from '@/new/components/icon';

type Side = 'left' | 'right';

// Persist width across open/close cycles, per side
const lastWidthBySide: Record<Side, number> = {
  left: window.innerWidth * 0.6,
  right: window.innerWidth * 0.6,
};

export function useDrawerResize(side: Side) {
  const [width, setWidth] = useState(lastWidthBySide[side]);
  const [isResizing, setIsResizing] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = side === 'left' ? e.clientX : window.innerWidth - e.clientX;
      const maxWidth = window.innerWidth * 0.9;
      const minWidth = window.innerWidth * 0.3;

      const constrainedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);
      lastWidthBySide[side] = constrainedWidth;
      setWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, side]);

  return { width, isResizing, handleMouseDown };
}

interface DrawerResizeHandleProps {
  side: Side;
  isResizing: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
}

export function DrawerResizeHandle({ side, isResizing, onMouseDown }: DrawerResizeHandleProps) {
  // Handle sits on the inward-facing edge of the drawer
  const positionClass = side === 'left' ? '-right-2.5' : '-left-2.5';
  return (
    <div onMouseDown={onMouseDown} className={`absolute top-0 ${positionClass} z-40 h-full w-5 cursor-ew-resize`}>
      <div
        className={`absolute top-1/2 left-1/2 z-20 flex h-7 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-0.75 rounded-md border border-gray-300 duration-150 hover:bg-gray-100 ${isResizing ? 'bg-gray-100' : 'bg-white'}`}
      >
        <Icon className="lucide--chevrons-left-right text-xs text-gray-500" />
      </div>
    </div>
  );
}
